// University of Miami: public college page (#2652, round 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UM's Common Data Set 2025-2026 (Institutional Research, Strategy and
//    Analytics; cds2526.pdf, linked from the IRSA Common Data Set page): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 plans. Quirk: the PDF's
//    figures are drawn so text extraction returns no numbers; they were read
//    from the rendered pages (CDS pages 13, 14, 17 and 23) and checked
//    against an OCR pass. The C21 early decision counts sit under the form's
//    printed "For the Fall 2024 entering class" label, so no early decision
//    rate is shown for fall 2025.
//  - UM Undergraduate Admission: Admission Plans and Deadlines ("Fall 2027
//    First-Year": deadlines, decision notification, binding terms, supplemental
//    materials for Architecture, Music and the BFA programs) and Testing
//    Policy for Undergraduate Admission.
//  - UM Office of Student Financial Assistance, 2026-2027 Cost of
//    Attendance, undergraduate on campus. The CDS G1 gives the same tuition,
//    fees, housing and meal plan, but its G0 box says 2026-2027 costs were
//    not final, and its G5 other expenses ($2,332) differ from the financial
//    aid page's personal allowance ($2,294); the financial aid page is used.
//  - College Scorecard API, school id 135726 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Miami Common Data Set 2025-2026',
        url: 'https://irsa.miami.edu/facts-and-information/common-data-set/index.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UM Undergraduate Admission, Admission Plans and Deadlines',
        url: 'https://admissions.miami.edu/undergraduate/application-process/options-and-deadlines/index.html',
        read: '2026-10-01',
    },
    testing: {
        label: 'UM Undergraduate Admission, Testing Policy',
        url: 'https://admissions.miami.edu/undergraduate/application-process/admission-requirements/testing-policy/index.html',
        read: '2026-10-01',
    },
    cost: {
        label: 'UM Office of Student Financial Assistance, 2026-2027 Cost of Attendance',
        url: 'https://finaid.miami.edu/cost/index.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?135726-University-of-Miami',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-miami',
    kbId: 'university_of_miami',
    name: 'University of Miami',
    shortName: 'UM',
    aliases: ['UM', 'Miami'],
    location: 'Coral Gables, FL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UM brand site); decoration only.
    branding: {
        primary: '#F47321',
        secondary: '#005030',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_miami_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 58167,
        admitted: 10245,
        enrolled: 2686,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 13608, admitted: 3155 },
            { group: 'Out-of-state', applied: 36007, admitted: 6282 },
            { group: 'International', applied: 8552, admitted: 808 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UM\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set gives 2,395 early decision applications and 1,062 admitted, but under the form\'s label for the fall 2024 entering class, so no early decision rate is shown here for fall 2025. It lists a nonbinding, non-restrictive early action plan but gives no early action counts.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a UM school or college. Some programs ask for more:',
        rows: [
            { school: 'School of Architecture', requirement: 'First-year applicants who do not send test scores must send a portfolio, due by the final credentials deadline of their plan.' },
            { school: 'Frost School of Music', requirement: 'A supplemental application, a prescreen and an audition, due November 1 for Early Action and December 1 for Regular Decision. Frost Early Decision II and Regular Decision applicants must apply by December 1. Audio Technology (BS) and Music Industry (BA) applicants skip the audition but must send test scores.' },
            { school: 'Art BFA (College of Arts and Sciences)', requirement: 'First-year applicants who do not send test scores must send a portfolio: a PDF of 15 to 20 images with a one-page artist statement.' },
            { school: 'Theatre Arts BFA (College of Arts and Sciences)', requirement: 'Extra materials whether or not you send test scores: a portfolio for Stage Management and Design/Technical Production, and an Audition Request Form with a fee and an audition for Musical Theatre.' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late January', binding: false },
            { plan: 'Early Decision II', deadline: 'January 5', decision: 'Late February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'April 1', binding: false },
        ],
        note: 'Test scores and every supporting document are due by each plan\'s final credentials deadline: November 13 (Early Decision I), November 20 (Early Action), January 25 (Early Decision II) and February 5 (Regular Decision). An early application that is incomplete by then moves to Regular Decision. The financial aid deadline is November 15 for Early Decision I and Early Action and January 5 for the other plans; every applicant for aid completes the CSS Profile. Early Decision I and Early Action applicants are considered for the Premier Scholarships as well as merit scholarships.',
        source: 'deadlines',
        binding: 'Both Early Decision plans are binding, for applicants committed to attending UM if admitted, and each requires the Early Decision Agreement form, available through the Common Application.',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'UM requires SAT or ACT scores from most applicants, as it has since fall 2026 entry. You enter your scores on the Common Application or in the Applicant Portal by your plan\'s final credentials deadline; only students who are admitted and enroll send official scores. Applicants to performance-based Frost School of Music programs, the BFA programs in the College of Arts and Sciences and the School of Architecture, students graduating from a high school outside the United States, and transfer applicants with 24 or more hours of college work may apply with or without scores.',
        source: 'testing',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for admission for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 66312 },
            { item: 'Fees', amount: 2030 },
            { item: 'Housing (on campus)', amount: 16958 },
            { item: 'Meals', amount: 9400 },
            { item: 'Books', amount: 1040 },
            { item: 'Personal', amount: 2294 },
            { item: 'Transportation', amount: 800 },
            { item: 'Loan fees', amount: 38 },
        ],
        total: 98872,
        note: 'For an undergraduate living on campus. UM calls these figures preliminary until its Board of Trustees approves them. International students\' total is $104,091, which adds the required health insurance and a higher transportation allowance. Students from Alaska and Hawaii get a $1,600 transportation allowance.',
        source: 'cost',
        netPrice: {
            average: 36483,
            byIncome: [
                { income: '$0 to $30,000', amount: 14575 },
                { income: '$30,001 to $48,000', amount: 13020 },
                { income: '$48,001 to $75,000', amount: 19695 },
                { income: '$75,001 to $110,000', amount: 25484 },
                { income: 'Over $110,000', amount: 51155 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UM\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
